import { LayoutDashboard, Shield, Trash2, UserPlus, Users } from '@tamagui/lucide-icons';
import { keepPreviousData, useQueryClient } from '@tanstack/react-query';
import { ScrollViewStyles } from '@unistocks/components/basic/scrollview';
import { Button } from '@unistocks/components/button/button';
import { Confirm } from '@unistocks/components/confirm/confirm';
import { Input } from '@unistocks/components/input/input';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Page } from '@unistocks/components/page/page';
import { Paginator } from '@unistocks/components/paginator/paginator';
import { asyncStateTransition } from '@unistocks/components/query-state/async-state';
import { QueryState } from '@unistocks/components/query-state/query-state';
import { getLocation, Table } from '@unistocks/components/table/table';
import { Paragraph } from '@unistocks/components/text/text';
import { toast } from '@unistocks/components/toast';
import { Tooltip } from '@unistocks/components/tooltip/tooltip';
import { PermissionManagementSheet } from '@unistocks/features/admin/components/permission-management/permission-management-sheet';
import { CreateRoleSheet } from '@unistocks/features/admin/components/role-management/create-role-sheet';
import { IdentityRoleDto } from '@unistocks/proxy/axios-client';
import {
  roleGetListQueryKey,
  useRoleDeleteMutationWithParameters,
  useRoleGetListQuery
} from '@unistocks/proxy/axios-client/RoleQuery';
import { MAX_RESULT_COUNT } from '@unistocks/shared/consts';
import { usePermission } from '@unistocks/shared/hooks/use-permission';
import { t } from '@unistocks/shared/localization/index.ts';
import { UnistocksPermissions } from '@unistocks/shared/permissions';
import { useRouter } from 'expo-router';
import React, { useState } from 'react';
import { View } from 'tamagui';

export const RoleList: React.FC = () => {
  const queryClient = useQueryClient();
  const router = useRouter();
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [permissionRole, setPermissionRole] = useState<IdentityRoleDto | null>(null);

  const [createRoleOpen, setCreateRoleOpen] = useState(false);
  const hasCreateRole = usePermission(UnistocksPermissions.RolesCreate);
  const hasDeleteRole = usePermission(UnistocksPermissions.RolesDelete);
  const hasManagePermissions = usePermission(UnistocksPermissions.RolesManagePermissions);

  const { data, isSuccess, isRefetching, refetch } = useRoleGetListQuery(
    searchText || undefined,
    undefined,
    (currentPage - 1) * MAX_RESULT_COUNT,
    MAX_RESULT_COUNT,
    undefined,
    { placeholderData: keepPreviousData }
  );

  const { mutate: deleteRole, isPending: isDeleting } = useRoleDeleteMutationWithParameters({
    onSuccess: () => {
      toast.success(t('AbpUi::SavedSuccessfully'));
      queryClient.invalidateQueries({ queryKey: roleGetListQueryKey() });
    },
    onError: () => {
      toast.error(t('AbpUi::DefaultErrorMessage'));
    },
    onSettled: () => {
      setDeletingId(null);
    }
  });

  const roles: IdentityRoleDto[] = isSuccess ? data.items : [];

  const handleSearch = (text: string) => {
    setSearchText(text);
    setCurrentPage(1);
  };

  const handleDelete = (roleId: string) => {
    setDeletingId(roleId);
    deleteRole({ id: roleId });
  };

  const handleRoleCreated = () => {
    setCurrentPage(1);
    queryClient.invalidateQueries({ queryKey: roleGetListQueryKey() });
  };

  return (
    <Page size="medium">
      <Page.ScrollView
        headerTitle={t('AbpIdentity::Roles')}
        headerIcon={
          hasCreateRole ? (
            <Button size={'$3'} Icon={UserPlus} onPress={() => setCreateRoleOpen(true)}>
              {t('AbpIdentity::NewRole')}
            </Button>
          ) : undefined
        }
        refreshing={isRefetching}
        onRefresh={refetch}
        breadcrumbs={[
          {
            key: 'admin',
            href: '/(admin)',
            name: t('::Menu:Administration'),
            rootPathName: 'admin',
            Icon: props => <LayoutDashboard {...props} />
          }
        ]}
        contentContainerStyle={ScrollViewStyles.contentContainerStyle}
      >
        <Column gap={'$3'} mt={'$3'}>
          <Input
            placeholder={t('AbpUi::PagerSearch')}
            value={searchText}
            onChangeText={handleSearch}
            $md={{
              size: '$3'
            }}
          />

          <Table
            transition="quickerLessBouncy"
            animateOnly={['opacity', 'transform']}
            enterStyle={{ o: 0, y: -20 }}
          >
            <Table.Head>
              <Table.Row>
                <Table.HeaderCell cellLocation="first">
                  <Table.HeaderCellText>{t('AbpIdentity::RoleName')}</Table.HeaderCellText>
                </Table.HeaderCell>
                <Table.HeaderCell
                  cellLocation="middle"
                  alignHeaderCells={{ x: 'end' }}
                  cellWidth={'$2'}
                >
                  <Table.HeaderCellText>
                    {t('AbpIdentity::DisplayName:IsDefault')}
                  </Table.HeaderCellText>
                </Table.HeaderCell>
                <Table.HeaderCell
                  cellLocation="middle"
                  alignHeaderCells={{ x: 'end' }}
                  cellWidth={'$2'}
                >
                  <Table.HeaderCellText>
                    {t('AbpIdentity::DisplayName:IsPublic')}
                  </Table.HeaderCellText>
                </Table.HeaderCell>
                <Table.HeaderCell
                  cellLocation="middle"
                  alignHeaderCells={{ x: 'end' }}
                  cellWidth={'$2'}
                >
                  <Table.HeaderCellText>
                    {t('AbpIdentity::DisplayName:IsStatic')}
                  </Table.HeaderCellText>
                </Table.HeaderCell>
                <Table.HeaderCell
                  cellLocation="last"
                  alignHeaderCells={{ x: 'end' }}
                  cellWidth={'$5'}
                >
                  <Table.HeaderCellText>{t('AbpUi::Actions')}</Table.HeaderCellText>
                </Table.HeaderCell>
              </Table.Row>
            </Table.Head>

            <QueryState
              queryKeys={[
                roleGetListQueryKey(
                  searchText || undefined,
                  undefined,
                  (currentPage - 1) * MAX_RESULT_COUNT,
                  MAX_RESULT_COUNT,
                  undefined
                )
              ]}
              loadingText={t('AbpUi::LoadingWithThreeDot')}
              isEmpty={isSuccess && !roles.length}
              noDataIcon={Users}
              noDataText={t('::NoDataAvailableInDatatable')}
            >
              <Table.Body key={'table-body'} {...asyncStateTransition}>
                {isSuccess &&
                  roles.map((role, i, arr) => (
                    <Table.Row
                      key={role.id}
                      pressable
                      rowLocation={getLocation(i, arr)}
                      onPress={() =>
                        router.push({
                          pathname: '/(admin)/role-management/[id]',
                          params: { id: role.id }
                        })
                      }
                    >
                      <Table.Cell cellLocation="first">
                        <Paragraph>{role.name}</Paragraph>
                      </Table.Cell>
                      <Table.Cell cellLocation="middle" alignCells={{ x: 'end' }} cellWidth={'$2'}>
                        <View
                          px={'$2'}
                          py={'$1'}
                          borderRadius={'$2'}
                          bg={role.isDefault ? '$green8' : '$gray8'}
                        >
                          <Paragraph color={'$white1'} size={'$3'}>
                            {role.isDefault ? t('AbpUi::Yes') : t('AbpUi::No')}
                          </Paragraph>
                        </View>
                      </Table.Cell>
                      <Table.Cell cellLocation="middle" alignCells={{ x: 'end' }} cellWidth={'$2'}>
                        <View
                          px={'$2'}
                          py={'$1'}
                          borderRadius={'$2'}
                          bg={role.isPublic ? '$green8' : '$gray8'}
                        >
                          <Paragraph color={'$white1'} size={'$3'}>
                            {role.isPublic ? t('AbpUi::Yes') : t('AbpUi::No')}
                          </Paragraph>
                        </View>
                      </Table.Cell>
                      <Table.Cell cellLocation="middle" alignCells={{ x: 'end' }} cellWidth={'$2'}>
                        <Tooltip
                          label={`${t('AbpIdentity::Volo.Abp.Identity:010005')} ${t('AbpIdentity::Volo.Abp.Identity:010006')}`}
                          placement="top"
                          disabled={!role.isStatic}
                        >
                          <View
                            px={'$2'}
                            py={'$1'}
                            borderRadius={'$2'}
                            bg={role.isStatic ? '$orange8' : '$gray8'}
                          >
                            <Paragraph color={'$white1'} size={'$3'}>
                              {role.isStatic ? t('AbpUi::Yes') : t('AbpUi::No')}
                            </Paragraph>
                          </View>
                        </Tooltip>
                      </Table.Cell>
                      <Table.ActionsCell cellWidth={'$5'}>
                        <Row gap={'$2'}>
                          {hasManagePermissions && (
                            <Button
                              size={'$2'}
                              variant={'outlined'}
                              minW={'$4'}
                              Icon={Shield}
                              onPress={() => setPermissionRole(role)}
                            />
                          )}
                          {hasDeleteRole && !role.isStatic && (
                            <Confirm
                              title={t('::DeleteWhat', { 0: role.name })}
                              message={t('AbpIdentity::RoleDeletionConfirmationMessage', {
                                0: role.name
                              })}
                              onConfirm={() => handleDelete(role.id)}
                              isLoading={isDeleting && deletingId === role.id}
                            >
                              <Button
                                size={'$2'}
                                theme="red"
                                minW={'$4'}
                                Icon={Trash2}
                                loading={isDeleting && deletingId === role.id}
                              />
                            </Confirm>
                          )}
                        </Row>
                      </Table.ActionsCell>
                    </Table.Row>
                  ))}
              </Table.Body>

              <Table.Foot key={'table-foot'} {...asyncStateTransition}>
                <Table.Row>
                  <Table.Cell alignCells={{ x: 'center' }}>
                    <Paginator
                      flex={1}
                      itemsPerPage={MAX_RESULT_COUNT}
                      currentPage={currentPage}
                      totalItems={data?.totalCount ?? 0}
                      onPageChange={setCurrentPage}
                    />
                  </Table.Cell>
                </Table.Row>
              </Table.Foot>
            </QueryState>
          </Table>
        </Column>
      </Page.ScrollView>

      <CreateRoleSheet
        open={createRoleOpen}
        onOpenChange={setCreateRoleOpen}
        onCreated={handleRoleCreated}
      />

      <PermissionManagementSheet
        providerName="R"
        providerKey={permissionRole?.name ?? ''}
        open={!!permissionRole}
        onOpenChange={open => {
          if (!open) setPermissionRole(null);
        }}
      />
    </Page>
  );
};

RoleList.displayName = 'RoleList';
